import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "../../styles/emailBodyEditor.css";
import { useParams } from "react-router-dom";
import { useMessageModal } from "../../contexts/MessageModalContext";

export default function EmailBodyEditor() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();

  const navigate = useNavigate();
  const { id } = useParams();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");

  useEffect(() => {
    if (!id) return;

    const fetchEmailBody = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/admin/settings/email-bodies/${id}`,
          { credentials: "include" },
        );

        const result = await res.json();

        if (result.success) {
          setName(result.data.name);
          setStatus(result.data.status);

          if (quillRef.current) {
            quillRef.current.root.innerHTML = result.data.body;
          }
        }
      } catch (error) {
        console.error("Fetch Email Body Error:", error);
      }
    };

    fetchEmailBody();
  }, [id]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          [{ align: [] }],
          ["clean"],
        ],
      },
    });
  }, []);

  const handleSave = async () => {
    const html = quillRef.current?.root.innerHTML;

    try {
      const method = id ? "PUT" : "POST";

      const url = id
        ? `${BASE_URL}/admin/settings/email-bodies/${id}`
        : `${BASE_URL}/admin/settings/email-bodies`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          body: html,
          status,
        }),
      });

      const result = await res.json();

      if (result.success) {
        showModal({
          type: "success",
          title: "Success",
          message: id
            ? "Email body updated successfully."
            : "Email body created successfully.",
          confirmText: "OK",
          onConfirm: () => navigate("/admin/email-bodies"),
        });
      }
    } catch (error) {
      console.error("Save Email Body Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to save email body.",
        confirmText: "OK",
      });
    }
  };

  return (
    <div className="email-editor-page">
      <h2>{id ? "Edit Email Body" : "Create Email Body"}</h2>

      <div className="editor-form">
        <div className="form-group">
          <label>Body Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Example: Order Confirmation Email"
          />
        </div>

        <div className="form-group">
          <label>Email Body</label>

          <div
            ref={editorRef}
            style={{
              height: "400px",
              background: "#fff",
              marginBottom: "30px",
            }}
          />
        </div>

        <div className="form-group">
          <label>Status</label>

          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="active"
                checked={status === "active"}
                onChange={() => setStatus("active")}
              />
              Active
            </label>

            <label>
              <input
                type="radio"
                value="inactive"
                checked={status === "inactive"}
                onChange={() => setStatus("inactive")}
              />
              Inactive
            </label>
          </div>
        </div>

        <div className="editor-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate("/admin/email-bodies")}
          >
            Cancel
          </button>

          <button className="btn-primary" onClick={handleSave}>
            Save Email Body
          </button>
        </div>
      </div>
    </div>
  );
}
